import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  Grid,
  Avatar,
  Tooltip,
  Button,
  IconButton,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EditIcon from "@mui/icons-material/Edit";
import SecurityIcon from "@mui/icons-material/Security";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  getAdminAccountsList,
  changeAdminAccountStatus,
} from "../../../../services/Admin/AdminUsersAccountsApi";
import AddAccountDialog from "./AddAccountDialog";
import EditeAccountDialog from "./EditeAccountDialog";
import AddPermissionsDialog from "./AddPermissionsDialog";
import AccountDetailsDialog from "./AccountDetailsDialog";
import { useAuth } from "../../../../Context/AuthContext";

const roles = [
  { value: "admin", label: "مدير نظام" },
  { value: "user", label: "محاسب" },
  { value: "captain_order", label: "كابتن اوردر" },
  { value: "delivery", label: "ديليفري" },
  { value: "cashier", label: "الكاشير" },
  { value: "kitchen", label: "المطبخ" },
  { value: "manager", label: "مدير" },
  { value: "head_manager", label: "مدير عام" },
];

const AdminAccountsMainPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [openPermissionsDialog, setOpenPermissionsDialog] = useState(false);
  const [selectedAdminForPermissions, setSelectedAdminForPermissions] = useState(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedAdminId, setSelectedAdminId] = useState(null);
  const { userRole, hasPermission } = useAuth();

  const getRoleLabel = (roleValue) => {
    return roles.find((role) => role.value === roleValue)?.label || roleValue;
  };

  useEffect(() => {
    fetchAdminAccounts();
  }, []);

  const fetchAdminAccounts = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getAdminAccountsList();
      setAdmins(response.data || []);
    } catch (err) {
      console.error("خطأ في جلب حسابات الأدمن:", err);
      setError(err.message || "حدث خطأ في جلب حسابات الأدمن");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAccountStatus = async (e, admin) => {
    e.stopPropagation();

    setStatusLoadingId(admin.id);
    setError("");
    setSuccessMessage("");

    try {
      await changeAdminAccountStatus(admin.id);
      const actionText = admin.is_active === 1 ? "إيقاف" : "تنشيط";
      setSuccessMessage(`تم ${actionText} الحساب بنجاح: ${admin.name}`);
      await fetchAdminAccounts();
    } catch (err) {
      console.error("خطأ في تغيير حالة الحساب:", err);
      setError(err.message || "حدث خطأ أثناء تغيير حالة الحساب");
    } finally {
      setStatusLoadingId(null);
    }
  };

  const getPermissionChips = (permissions) => {
    if (!permissions) return null;

    const permissionsList = [
      { key: "add", label: "إضافة", color: "success" },
      { key: "edit", label: "تعديل", color: "info" },
      { key: "delete", label: "حذف", color: "error" },
      { key: "view", label: "عرض", color: "primary" },
      { key: "print", label: "طباعة", color: "secondary" },
      { key: "export", label: "تصدير", color: "warning" },
    ];

    return permissionsList
      .filter((perm) => permissions[perm.key] === 1)
      .map((perm) => (
        <Chip
          key={perm.key}
          label={perm.label}
          size="small"
          color={perm.color}
          sx={{ mr: 0.5, mb: 0.5 }}
        />
      ));
  };

  const handleEditClick = (e, admin) => {
    e.stopPropagation();
    setSelectedAdmin(admin);
    setOpenEditDialog(true);
  };

  const handlePermissionsClick = (e, admin) => {
    e.stopPropagation();
    setSelectedAdminForPermissions(admin);
    setOpenPermissionsDialog(true);
  };

  const handleRowClick = (admin) => {
    setSelectedAdminId(admin.id);
    setOpenDetailsDialog(true);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  const activeAdmins = admins.filter((admin) => admin.is_active === 1).length;
  const inactiveAdmins = admins.filter((admin) => admin.is_active === 0).length;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Header with Add Button */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <AdminPanelSettingsIcon sx={{ fontSize: { xs: 30, md: 40 }, color: "primary.main" }} />
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: "bold" }}>
            حسابات الإدارة
          </Typography>
        </Box>
        {hasPermission("add") && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<PersonAddIcon />}
          onClick={() => setOpenAddDialog(true)}
          sx={{ whiteSpace: "nowrap", alignSelf: { xs: "stretch", md: "auto" } }}
        >
          إضافة حساب جديد
        </Button>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {/* Summary Cards */}
      {admins.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4} md={4}>
            <Card sx={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
                  إجمالي المديرين
                </Typography>
                <Typography variant="h3" sx={{ color: "white", fontWeight: "bold" }}>
                  {admins.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <Card sx={{ background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)" }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
                  الحسابات النشطة
                </Typography>
                <Typography variant="h3" sx={{ color: "white", fontWeight: "bold" }}>
                  {activeAdmins}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <Card sx={{ background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)" }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
                  الحسابات غير النشطة
                </Typography>
                <Typography variant="h3" sx={{ color: "white", fontWeight: "bold" }}>
                  {inactiveAdmins}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Admins Table */}
      {admins.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <AdminPanelSettingsIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد حسابات إدارة
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Grid container spacing={1.5}>
          {admins.map((admin) => (
            <Grid item xs={12} key={admin.id}>
              <Card
                onClick={() => handleRowClick(admin)}
                sx={{ cursor: "pointer", border: "1px solid", borderColor: "divider" }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar
                        sx={{
                          bgcolor: admin.is_active ? "success.main" : "error.main",
                          width: 36,
                          height: 36,
                        }}
                      >
                        {admin.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                          {admin.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          @{admin.username}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={admin.is_active === 1 ? "نشط" : "غير نشط"}
                      size="small"
                      color={admin.is_active === 1 ? "success" : "error"}
                    />
                  </Box>

                  <Box sx={{ mt: 1.2, display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                    <Chip label={getRoleLabel(admin.role)} size="small" color="primary" />
                    {admin.phone && <Chip label={admin.phone} size="small" variant="outlined" icon={<PhoneIcon />} />}
                  </Box>

                  {admin.address && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                      العنوان: {admin.address}
                    </Typography>
                  )}

                  <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", maxWidth: "100%" }}>
                    {getPermissionChips(admin.permissions)}
                  </Box>

                  <Box sx={{ mt: 1.2, display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                    <IconButton
                      size="small"
                      color="success"
                      onClick={(e) => handlePermissionsClick(e, admin)}
                    >
                      <SecurityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="info"
                      onClick={(e) => handleEditClick(e, admin)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color={admin.is_active === 1 ? "error" : "success"}
                      disabled={statusLoadingId === admin.id}
                      onClick={(e) => handleToggleAccountStatus(e, admin)}
                    >
                      {statusLoadingId === admin.id ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : admin.is_active === 1 ? (
                        <BlockIcon fontSize="small" />
                      ) : (
                        <CheckCircleIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 3, overflowX: "auto" }}>
          <Table dir="rtl" sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "primary.main" }}>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  المستخدم
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  اسم المستخدم
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  الهاتف
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  العنوان
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  الدور
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  الحالة
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  الصلاحيات
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  تاريخ الإنشاء
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "center" }}>
                  إجراءات
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.map((admin, index) => (
                <TableRow
                  key={admin.id}
                  onClick={() => handleRowClick(admin)}
                  sx={{
                    "&:hover": { backgroundColor: "action.hover", cursor: "pointer" },
                    backgroundColor: index % 2 === 0 ? "background.default" : "background.paper",
                  }}
                >
                  <TableCell sx={{ textAlign: "right" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar
                        sx={{
                          bgcolor: admin.is_active ? "success.main" : "error.main",
                          width: 40,
                          height: 40,
                        }}
                      >
                        {admin.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {admin.name}
                        </Typography>

                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    <Chip
                      label={admin.username}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    <Box>
                      {admin.phone && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">{admin.phone}</Typography>
                        </Box>
                      )}
                      {admin.phone2 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {admin.phone2}
                        </Typography>
                      )}
                      {admin.phone3 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {admin.phone3}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    <Box>
                      {admin.address && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                          <LocationOnIcon fontSize="small" color="action" />
                          <Typography variant="body2">{admin.address}</Typography>
                        </Box>
                      )}
                      {admin.address2 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {admin.address2}
                        </Typography>
                      )}
                      {admin.address3 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {admin.address3}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    <Chip
                      label={getRoleLabel(admin.role)}
                      size="small"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    {admin.is_active === 1 ? (
                      <Tooltip title="نشط" arrow>
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="نشط"
                          size="small"
                          color="success"
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip title="غير نشط" arrow>
                        <Chip
                          icon={<CancelIcon />}
                          label="غير نشط"
                          size="small"
                          color="error"
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", maxWidth: 200 }}>
                      {getPermissionChips(admin.permissions)}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    <Typography variant="body2">
                      {new Date(admin.created_at).toLocaleDateString("ar-EG", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(admin.created_at).toLocaleTimeString("ar-EG", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                      {hasPermission("add") && (
                      <Tooltip title="إدارة الصلاحيات" arrow>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={(e) => handlePermissionsClick(e, admin)}
                          sx={{
                            "&:hover": {
                              backgroundColor: "success.light",
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <SecurityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      )}
                      {hasPermission("edit") && (
                      <Tooltip title="تعديل الحساب" arrow>
                        <IconButton
                          size="small"
                          color="info"
                          onClick={(e) => handleEditClick(e, admin)}
                          sx={{
                            "&:hover": {
                              backgroundColor: "info.light",
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      )}

                      {hasPermission("delete") && (
                      <Tooltip
                        title={admin.is_active === 1 ? "إيقاف نشاط الحساب" : "تنشيط الحساب"}
                        arrow
                      >
                        <span>
                          <IconButton
                            size="small"
                            color={admin.is_active === 1 ? "error" : "success"}
                            disabled={statusLoadingId === admin.id}
                            onClick={(e) => handleToggleAccountStatus(e, admin)}
                            sx={{
                              "&:hover": {
                                backgroundColor:
                                  admin.is_active === 1 ? "error.light" : "success.light",
                                transform: "scale(1.1)",
                              },
                              transition: "all 0.2s",
                            }}
                          >
                            {statusLoadingId === admin.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <>
                                {admin.is_active === 1 ? (
                                  <BlockIcon fontSize="small" />
                                ) : (
                                  <CheckCircleIcon fontSize="small" />
                                )}
                              </>
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Account Dialog */}
      <AddAccountDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onAdded={() => {
          fetchAdminAccounts();
          setOpenAddDialog(false);
        }}
      />

      {/* Edit Account Dialog */}
      {selectedAdmin && (
        <EditeAccountDialog
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
          admin={selectedAdmin}
          onUpdated={() => {
            fetchAdminAccounts();
            setOpenEditDialog(false);
          }}
        />
      )}

      {/* Permissions Dialog */}
      {selectedAdminForPermissions && (
        <AddPermissionsDialog
          open={openPermissionsDialog}
          onClose={() => setOpenPermissionsDialog(false)}
          admin={selectedAdminForPermissions}
          onUpdated={() => {
            fetchAdminAccounts();
            setOpenPermissionsDialog(false);
          }}
        />
      )}

      {/* Details Dialog */}
      <AccountDetailsDialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        adminId={selectedAdminId}
      />
    </Box>
  );
};

export default AdminAccountsMainPage;
